/**
 * Shared discovery/identity contract (T003-INT, docs/IMPLEMENTATION_PLAN.md
 * "Route and filter contract" + "LIVE-CLIENT EPOCH CONTRACT").
 *
 * Pure data + pure functions, NO imports: server (SSR/redirects, api/) and
 * browser (SPA replace-navigation, buzzmap-web) both execute this exact code
 * so their normalized URLs and descriptors can never diverge (R9).
 *
 * Format translation vs geographic semantics — the core distinction of this
 * module: `normalizeDiscoveryRequest` translates URL *format* (legacy keys,
 * query slugs, precedence, view/bbox canonicalization) and, on generic
 * entries, surfaces a typed "needs geographic resolution" outcome carrying
 * the legacy hint. It NEVER resolves geography itself — no database, no
 * registry, no guessing. A format translation is not proof that a client
 * understands the current geographic semantics: protocol compatibility is
 * proven separately by `negotiateDiscoveryProtocol` against the server's
 * capabilities (the LIVE-CLIENT EPOCH CONTRACT). The two must both pass
 * before a discovery query runs.
 *
 * Vocabulary note: `CATEGORY_SLUGS` is the SEO path vocabulary (11 entries,
 * `altro` deliberately absent — no category page, C2). The query vocabulary
 * additionally publishes `altro` as a query slug so `?categorie=altro` is a
 * valid public selection. `non_evento` is a classifier label and appears in
 * NEITHER vocabulary — it is never public.
 */

// ---------------------------------------------------------------------------
// Category vocabulary (single source of truth; api/ and web/ re-export this)
// ---------------------------------------------------------------------------

export interface CategorySlug {
  id: string;
  slug: string;
  label: string;
}

/** SEO path vocabulary — byte-identical to the historical api/web twins. */
export const CATEGORY_SLUGS: CategorySlug[] = [
  { id: 'mercatino', slug: 'mercatini', label: 'Mercatini' },
  { id: 'sagra', slug: 'sagre', label: 'Sagre' },
  { id: 'fiera', slug: 'fiere', label: 'Fiere' },
  { id: 'arte', slug: 'mostre', label: 'Mostre e arte' },
  { id: 'musica', slug: 'musica', label: 'Musica e concerti' },
  { id: 'bambini', slug: 'bambini', label: 'Eventi per bambini' },
  { id: 'sport', slug: 'sport', label: 'Eventi sportivi' },
  { id: 'cinema_teatro', slug: 'teatro-e-spettacoli', label: 'Teatro e spettacoli' },
  { id: 'festivita', slug: 'festivita', label: 'Festività' },
  { id: 'tradizione', slug: 'feste-e-tradizioni', label: 'Feste e tradizioni' },
  { id: 'rievocazione', slug: 'rievocazioni-storiche', label: 'Rievocazioni storiche' },
];

/**
 * Every id the `event_category` DB enum can carry (schema.sql + the v4
 * migration 20260514_add_arte_category.sql). Deliberately broader than
 * CATEGORY_SLUGS: `altro` is a valid event category but has no SEO page.
 * `non_evento` is a classifier label, not a category, and is not listed.
 */
export const EVENT_CATEGORY_IDS: readonly string[] = [
  'mercatino',
  'sagra',
  'fiera',
  'arte',
  'musica',
  'bambini',
  'sport',
  'cinema_teatro',
  'festivita',
  'tradizione',
  'rievocazione',
  'altro',
];

/** True when `slug` names an SEO category page path segment. */
export function isCategorySlug(slug: string): boolean {
  return CATEGORY_SLUGS.some((c) => c.slug === slug);
}

export function categoryBySlug(slug: string): CategorySlug | undefined {
  return CATEGORY_SLUGS.find((c) => c.slug === slug);
}

export function categoryById(id: string): CategorySlug | undefined {
  return CATEGORY_SLUGS.find((c) => c.id === id);
}

/**
 * True when `id` is a valid event category (including `altro`).
 * Exact match, case-sensitive — DB enum values.
 */
export function isEventCategoryId(id: string): boolean {
  return EVENT_CATEGORY_IDS.includes(id);
}

/** Public query slug for a category id (SEO slug, or `altro` itself). */
export function categoryIdToQuerySlug(id: string): string | undefined {
  if (!isEventCategoryId(id)) return undefined;
  const seo = categoryById(id);
  return seo ? seo.slug : id;
}

/** Category id for a public query slug. */
export function querySlugToCategoryId(slug: string): string | undefined {
  const seo = categoryBySlug(slug);
  if (seo) return seo.id;
  if (slug === 'altro') return 'altro';
  return undefined;
}

/** True when `slug` is valid in `?categorie=` (SEO slugs + `altro`). */
export function isQueryCategorySlug(slug: string): boolean {
  return querySlugToCategoryId(slug) !== undefined;
}

// ---------------------------------------------------------------------------
// Period vocabulary (single source of truth; api/ and web/ re-export this)
// ---------------------------------------------------------------------------

/**
 * The 14 period slugs of the SEO period pages (wave 3 of
 * docs/IMPLEMENTATION_PLAN_SEO_PERIODS_CAMPAIGNS.md): `oggi`,
 * `questo-weekend` and the twelve months. Canonical data lives here so the
 * path grammar (`parseDiscoveryPath`), the `?quando=` query vocabulary and
 * the api/web twins can never drift — same arrangement as CATEGORY_SLUGS.
 * No period slug collides with a category slug or a territory slug
 * (guarded by db/migrations/013_period_slug_guard.sql).
 */
export interface PeriodSlug {
  slug: string;
  label: string;
}

export const PERIOD_SLUGS: PeriodSlug[] = [
  { slug: 'oggi', label: 'Oggi' },
  { slug: 'questo-weekend', label: 'Questo weekend' },
  { slug: 'gennaio', label: 'Gennaio' },
  { slug: 'febbraio', label: 'Febbraio' },
  { slug: 'marzo', label: 'Marzo' },
  { slug: 'aprile', label: 'Aprile' },
  { slug: 'maggio', label: 'Maggio' },
  { slug: 'giugno', label: 'Giugno' },
  { slug: 'luglio', label: 'Luglio' },
  { slug: 'agosto', label: 'Agosto' },
  { slug: 'settembre', label: 'Settembre' },
  { slug: 'ottobre', label: 'Ottobre' },
  { slug: 'novembre', label: 'Novembre' },
  { slug: 'dicembre', label: 'Dicembre' },
];

/** True when `slug` names a period page path segment / `?quando=` value. */
export function isPeriodSlug(slug: string): boolean {
  return PERIOD_SLUGS.some((period) => period.slug === slug);
}

export function periodBySlug(slug: string): PeriodSlug | undefined {
  return PERIOD_SLUGS.find((period) => period.slug === slug);
}

// ---------------------------------------------------------------------------
// Route grammar
// ---------------------------------------------------------------------------

/**
 * A parsed discovery route. `segments` are the territory slugs under
 * `/eventi` (1 = region|province, 2 = province/comune); whether a 1-segment
 * slug is a region or a province is registry data — resolved server-side.
 */
export type DiscoveryRoute =
  | { kind: 'generic-home' }
  | { kind: 'generic-esplora' }
  | { kind: 'territory-home'; segments: string[] }
  | { kind: 'territory-esplora'; segments: string[] }
  | { kind: 'territory-category'; segments: string[]; categorySlug: string }
  /**
   * Period page (upstream wave 3): `{provinceOrRegion}/{period}` or
   * `{province}/{comune}/{period}`. `segments` are the territory slugs only;
   * `periodSlug` is the trailing path period. There is no category × period
   * shape (C4) and no esplora × period shape — both keep `?quando=` in the
   * query instead.
   */
  | { kind: 'territory-period'; segments: string[]; periodSlug: string };

export type RouteParseOutcome =
  | { kind: 'ok'; route: DiscoveryRoute }
  /** Malformed shape (bad segments, extra segments, reserved misuse). */
  | { kind: 'invalid'; reason: string }
  /** Shape-valid but the category token in category position is unknown → 404 contract. */
  | { kind: 'not-found'; reason: string };

/** Same shape the server enforces on territory slugs (api/lib/territory.ts). */
const SEGMENT_RE = /^[a-z0-9-]{2,60}$/;

/** Reserved view suffix — recognized before category/comune ambiguity. */
export const ESPLORA_RESERVED_SEGMENT = 'esplora';

function stripTrailingSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/**
 * Parses a pathname (no query string) into a typed discovery route.
 * Grammar (upstream precedence "category > period > comune"):
 *   /                          → generic-home
 *   /esplora                   → generic-esplora
 *   /eventi/{a}                → territory-home (region|province)
 *   /eventi/{a}/esplora        → territory-esplora   (reserved suffix wins over comune)
 *   /eventi/{a}/{cat}          → territory-category  (category wins over comune)
 *   /eventi/{a}/{per}          → territory-period    (period wins over comune)
 *   /eventi/{a}/{b}            → territory-home (province/comune)
 *   /eventi/{a}/{b}/esplora    → territory-esplora   (only if b is not a category/period)
 *   /eventi/{a}/{b}/{cat}      → territory-category  (only if b is not a category/period)
 *   /eventi/{a}/{b}/{per}      → territory-period    (only if b is not a category/period)
 * Anything else — extra segments, category/period in the middle, reserved
 * suffix as a territory, bad charset, empty segments — is `invalid`; an
 * unknown token in the trailing category/period position is `not-found`.
 * Never a generic fallback.
 */
export function parseDiscoveryPath(pathname: string): RouteParseOutcome {
  const clean = stripTrailingSlash(pathname.split('?')[0] ?? pathname);
  if (clean === '/') return { kind: 'ok', route: { kind: 'generic-home' } };
  if (clean === `/${ESPLORA_RESERVED_SEGMENT}`) {
    return { kind: 'ok', route: { kind: 'generic-esplora' } };
  }
  if (!clean.startsWith('/eventi/') && clean !== '/eventi') {
    return { kind: 'invalid', reason: 'not-a-discovery-path' };
  }

  // '/eventi'.slice(7) === '' ; '/eventi/a'.slice(7) === '/a'
  const rawSegments = clean.slice('/eventi'.length).split('/');
  const segments: string[] = [];
  let skippedLeadingSlash = false;
  for (const raw of rawSegments) {
    if (raw === '') {
      // Exactly one empty piece is legitimate (the '/' right after /eventi).
      // Any other empty piece ('/eventi//crema') is malformed.
      if (!skippedLeadingSlash) {
        skippedLeadingSlash = true;
        continue;
      }
      return { kind: 'invalid', reason: 'empty-segment' };
    }
    let decoded: string;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      return { kind: 'invalid', reason: 'undecodable-segment' };
    }
    segments.push(decoded);
  }

  if (segments.length === 0 || segments.length > 3) {
    return { kind: 'invalid', reason: 'wrong-segment-count' };
  }
  for (const segment of segments) {
    if (!SEGMENT_RE.test(segment)) return { kind: 'invalid', reason: 'bad-segment-charset' };
  }

  if (segments.length === 1) {
    const a = segments[0]!;
    if (a === ESPLORA_RESERVED_SEGMENT) {
      return { kind: 'invalid', reason: 'reserved-esplora-as-territory' };
    }
    return { kind: 'ok', route: { kind: 'territory-home', segments: [a] } };
  }

  if (segments.length === 2) {
    const a = segments[0]!;
    const b = segments[1]!;
    if (b === ESPLORA_RESERVED_SEGMENT) {
      return { kind: 'ok', route: { kind: 'territory-esplora', segments: [a] } };
    }
    if (isCategorySlug(b)) {
      return { kind: 'ok', route: { kind: 'territory-category', segments: [a], categorySlug: b } };
    }
    if (isPeriodSlug(b)) {
      return { kind: 'ok', route: { kind: 'territory-period', segments: [a], periodSlug: b } };
    }
    return { kind: 'ok', route: { kind: 'territory-home', segments: [a, b] } };
  }

  // 3 segments: neither a category nor a period may appear in the middle
  // (upstream rule: a period/category token there would masquerade as a
  // territory, and no slug may ever be ambiguous between the two vocabularies).
  const a = segments[0]!;
  const b = segments[1]!;
  const c = segments[2]!;
  if (isCategorySlug(b)) {
    return { kind: 'invalid', reason: 'category-in-middle' };
  }
  if (isPeriodSlug(b)) {
    return { kind: 'invalid', reason: 'period-in-middle' };
  }
  if (c === ESPLORA_RESERVED_SEGMENT) {
    return { kind: 'ok', route: { kind: 'territory-esplora', segments: [a, b] } };
  }
  if (isCategorySlug(c)) {
    return {
      kind: 'ok',
      route: { kind: 'territory-category', segments: [a, b], categorySlug: c },
    };
  }
  if (isPeriodSlug(c)) {
    return { kind: 'ok', route: { kind: 'territory-period', segments: [a, b], periodSlug: c } };
  }
  return { kind: 'not-found', reason: 'unknown-path-category' };
}

/** Builds the canonical pathname for a parsed route (inverse of the parser). */
export function buildDiscoveryPath(route: DiscoveryRoute): string {
  switch (route.kind) {
    case 'generic-home':
      return '/';
    case 'generic-esplora':
      return '/esplora';
    case 'territory-home':
      return `/eventi/${route.segments.join('/')}`;
    case 'territory-esplora':
      return `/eventi/${route.segments.join('/')}/${ESPLORA_RESERVED_SEGMENT}`;
    case 'territory-category':
      return `/eventi/${route.segments.join('/')}/${route.categorySlug}`;
    case 'territory-period':
      return `/eventi/${route.segments.join('/')}/${route.periodSlug}`;
  }
}

// ---------------------------------------------------------------------------
// Query grammar
// ---------------------------------------------------------------------------

export const RADIUS_KM_MIN = 1;
export const RADIUS_KM_MAX = 200;

/**
 * Map viewport in URL order west,south,east,north. NEVER rotated to
 * lat-first: the URL order is the contract (Rome: 12.4,41.7,12.6,41.9).
 */
export interface MapBbox {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface DiscoveryFilters {
  /** Calendar date YYYY-MM-DD (Europe/Rome calendar day, no conversion). */
  startDate?: string;
  /** Defaults to startDate when only start is given (same-day event). */
  endDate?: string;
  radiusKm?: number;
  search?: string;
}

export interface QueryIssue {
  param: string;
  reason: string;
}

/** A legacy geographic hint — NEVER resolved here, only carried typed. */
export type GeographicHint =
  | { kind: 'pair'; city: string; province?: string }
  | { kind: 'center'; lat: number; lng: number; total: number };

export interface ParsedDiscoveryQuery {
  /** Raw non-empty parts of `categorie` — vocabulary-checked by normalize. */
  categorySlugs: string[];
  hasCategorieParam: boolean;
  /** Raw ids from legacy `category=`, or null when absent. */
  legacyCategoryIds: string[] | null;
  /** Legacy `city` (+ optional `province`), or null when absent. */
  legacyGeo: { city: string; province?: string } | null;
  /** Legacy `centers` JSON (validated), or null when absent. Empty = no hint. */
  centers: Array<{ lat: number; lng: number }> | null;
  /** Legacy `bbox` / min|maxLat/Lng, already in west,south,east,north order. */
  legacyBbox: MapBbox | null;
  filters: DiscoveryFilters;
  view: 'list' | 'map';
  mapBbox: MapBbox | null;
  /**
   * A single valid `?quando=` period slug (upstream wave 3). Absent when the
   * key is missing, invalid, or repeated (an array value is ignored, exactly
   * like upstream's "array/valori invalidi ignorati"). An INVALID value is
   * not dropped — it stays in `unknownParams` (URL preserved, noindex).
   */
  queryPeriodSlug?: string;
  /** Recognized tracking params — survive verbatim, never affect identity. */
  trackingParams: Array<[string, string]>;
  /** Everything else — preserved verbatim, marks the URL non-indexable. */
  unknownParams: Array<[string, string]>;
}

export type QueryParseOutcome =
  | { kind: 'ok'; query: ParsedDiscoveryQuery }
  /** Syntax/range errors → recoverable invalid-filter state, never broader. */
  | { kind: 'invalid'; issues: QueryIssue[] };

/**
 * Recognized tracking parameters (no app code treated any param as tracking
 * before this contract — this set is the contract's own definition). They
 * survive normalization verbatim but never change query identity or
 * indexability.
 */
const TRACKING_PARAM_RE =
  /^(utm_[a-z0-9_]+|gclid|dclid|fbclid|msclkid|twclid|yclid|igshid|wbraid|gbraid|mc_cid|mc_eid|_hsenc|_hsmi|_ga|_gl|vero_id|srsltid)$/i;

const CANONICAL_QUERY_KEYS = new Set([
  'categorie',
  'category',
  'startDate',
  'endDate',
  'radiusKm',
  'search',
  'view',
  'mapBbox',
  'city',
  'province',
  'centers',
  'bbox',
  'minLat',
  'maxLat',
  'minLng',
  'maxLng',
]);

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function isValidCalendarDate(value: string): boolean {
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Parses and range-checks a west,south,east,north comma tuple. */
function parseBboxTuple(raw: string): MapBbox | null {
  const parts = raw.split(',');
  if (parts.length !== 4) return null;
  const nums = parts.map((p) => Number(p.trim()));
  if (!nums.every((n): n is number => Number.isFinite(n))) return null;
  const west = nums[0]!;
  const south = nums[1]!;
  const east = nums[2]!;
  const north = nums[3]!;
  if (south < -90 || south > 90 || north < -90 || north > 90) return null;
  if (west < -180 || west > 180 || east < -180 || east > 180) return null;
  if (south > north || west > east) return null;
  return { west, south, east, north };
}

/**
 * Upper bound for a legacy `centers` JSON array. The API's MAX_CENTERS is
 * 20 (every region preset fits); 100 leaves headroom while making the
 * parser's cost explicitly bounded (plan security control).
 */
const MAX_LEGACY_CENTERS = 100;

function parseCentersJson(raw: string): Array<{ lat: number; lng: number }> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  if (parsed.length > MAX_LEGACY_CENTERS) return null;
  const centers: Array<{ lat: number; lng: number }> = [];
  for (const item of parsed) {
    if (!item || typeof item !== 'object') return null;
    const lat = (item as Record<string, unknown>).lat;
    const lng = (item as Record<string, unknown>).lng;
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    centers.push({ lat, lng });
  }
  return centers;
}

/**
 * Parses and validates the query string (with or without leading `?`).
 * Syntax-level only: category vocabulary checks happen in
 * `normalizeDiscoveryRequest` where path-vs-query precedence is known.
 */
export function parseDiscoveryQuery(search: string): QueryParseOutcome {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const issues: QueryIssue[] = [];

  // --- duplicate reserved keys (plan security control: validate duplicates,
  // do not silently first-win; tracking/unknown keys may repeat) ---
  const reservedSeen = new Set<string>();
  for (const [key] of params.entries()) {
    if (CANONICAL_QUERY_KEYS.has(key)) {
      if (reservedSeen.has(key)) {
        issues.push({ param: key, reason: 'duplicate-reserved-param' });
      }
      reservedSeen.add(key);
    }
  }

  const categorieRaw = params.get('categorie');
  const categorySlugs = (categorieRaw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const legacyCategoryRaw = params.get('category');
  const legacyCategoryIds = legacyCategoryRaw
    ? legacyCategoryRaw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    : null;

  // --- dates (calendar days, Europe/Rome) ---
  const filters: DiscoveryFilters = {};
  const startDate = params.get('startDate');
  const endDate = params.get('endDate');
  if (startDate !== null) {
    if (!isValidCalendarDate(startDate)) {
      issues.push({ param: 'startDate', reason: 'invalid-calendar-date' });
    } else {
      filters.startDate = startDate;
    }
  }
  if (endDate !== null) {
    if (!isValidCalendarDate(endDate)) {
      issues.push({ param: 'endDate', reason: 'invalid-calendar-date' });
    } else {
      filters.endDate = endDate;
    }
  }
  if (filters.startDate && filters.endDate && filters.endDate < filters.startDate) {
    issues.push({ param: 'endDate', reason: 'range-reversed' });
  }
  if (filters.startDate && !filters.endDate) {
    // Missing end with a valid start = that same day.
    filters.endDate = filters.startDate;
  }

  // --- radius ---
  const radiusRaw = params.get('radiusKm');
  if (radiusRaw !== null) {
    const radius = Number(radiusRaw);
    if (!isFiniteNumber(radius) || radius < RADIUS_KM_MIN || radius > RADIUS_KM_MAX) {
      issues.push({ param: 'radiusKm', reason: 'out-of-range' });
    } else {
      filters.radiusKm = radius;
    }
  }

  // --- search ---
  const searchRaw = params.get('search');
  if (searchRaw !== null) {
    const trimmed = searchRaw.trim();
    if (trimmed.length > 0) filters.search = trimmed;
  }

  // --- view ---
  const viewRaw = params.get('view');
  let view: 'list' | 'map' = 'list';
  if (viewRaw !== null && viewRaw !== 'list' && viewRaw !== 'map') {
    issues.push({ param: 'view', reason: 'unknown-view' });
  } else if (viewRaw === 'map') {
    view = 'map';
  }

  // --- canonical mapBbox ---
  let mapBbox: MapBbox | null = null;
  const mapBboxRaw = params.get('mapBbox');
  if (mapBboxRaw !== null) {
    mapBbox = parseBboxTuple(mapBboxRaw);
    if (!mapBbox) issues.push({ param: 'mapBbox', reason: 'invalid-bbox' });
  }

  // --- legacy bbox: `bbox=west,south,east,north` or the four-field form ---
  let legacyBbox: MapBbox | null = null;
  const legacyBboxRaw = params.get('bbox');
  const fieldNames = ['minLat', 'maxLat', 'minLng', 'maxLng'] as const;
  const presentFields = fieldNames.filter((f) => params.get(f) !== null);
  if (legacyBboxRaw !== null) {
    legacyBbox = parseBboxTuple(legacyBboxRaw);
    if (!legacyBbox) issues.push({ param: 'bbox', reason: 'invalid-bbox' });
  } else if (presentFields.length > 0) {
    if (presentFields.length === fieldNames.length) {
      const minLat = Number(params.get('minLat'));
      const maxLat = Number(params.get('maxLat'));
      const minLng = Number(params.get('minLng'));
      const maxLng = Number(params.get('maxLng'));
      const four = parseBboxTuple(`${minLng},${minLat},${maxLng},${maxLat}`);
      if (!four) {
        issues.push({ param: 'minLat/maxLat/minLng/maxLng', reason: 'invalid-bbox' });
      } else {
        legacyBbox = four;
      }
    } else {
      issues.push({ param: 'minLat/maxLat/minLng/maxLng', reason: 'incomplete-bbox' });
    }
  }

  // --- legacy geographic hints ---
  let legacyGeo: { city: string; province?: string } | null = null;
  const cityRaw = params.get('city');
  if (cityRaw !== null && cityRaw.trim().length > 0) {
    const provinceRaw = params.get('province');
    if (provinceRaw !== null && provinceRaw.trim().length > 0) {
      legacyGeo = { city: cityRaw.trim(), province: provinceRaw.trim() };
    } else {
      legacyGeo = { city: cityRaw.trim() };
    }
  }
  let centers: Array<{ lat: number; lng: number }> | null = null;
  const centersRaw = params.get('centers');
  if (centersRaw !== null) {
    centers = parseCentersJson(centersRaw);
    if (!centers) issues.push({ param: 'centers', reason: 'invalid-centers-json' });
  }

  // --- period query (upstream R5: a single vocabulary string; an array or an
  // invalid value is ignored — the invalid one stays unknown below, so the URL
  // survives as a noindex variant instead of being silently rewritten) ---
  const quandoValues = params.getAll('quando');
  const queryPeriodSlug =
    quandoValues.length === 1 && isPeriodSlug(quandoValues[0] ?? '')
      ? (quandoValues[0] as string)
      : undefined;

  // --- tracking vs unknown params (preserve encounter order) ---
  const trackingParams: Array<[string, string]> = [];
  const unknownParams: Array<[string, string]> = [];
  for (const [key, value] of params.entries()) {
    if (TRACKING_PARAM_RE.test(key)) {
      trackingParams.push([key, value]);
    } else if (key === 'quando' && queryPeriodSlug !== undefined) {
      // the applied period: identity, never an unknown param (see above)
      continue;
    } else if (!CANONICAL_QUERY_KEYS.has(key)) {
      unknownParams.push([key, value]);
    }
  }

  if (issues.length > 0) return { kind: 'invalid', issues };
  return {
    kind: 'ok',
    query: {
      categorySlugs,
      hasCategorieParam: categorieRaw !== null,
      legacyCategoryIds,
      legacyGeo,
      centers,
      legacyBbox,
      filters,
      view,
      mapBbox,
      ...(queryPeriodSlug !== undefined && { queryPeriodSlug }),
      trackingParams,
      unknownParams,
    },
  };
}

// ---------------------------------------------------------------------------
// Descriptor + normalization outcomes
// ---------------------------------------------------------------------------

export interface DiscoveryDescriptor {
  route: DiscoveryRoute;
  /** Resolved category IDs (query-vocabulary order), empty = all categories. */
  categoryIds: string[];
  /** Where the category selection came from — path always wins over query. */
  categorySource: 'path' | 'query' | 'none';
  filters: DiscoveryFilters;
  view: 'list' | 'map';
  mapBbox: MapBbox | null;
  /**
   * Effective period of this page — the path period when the route is
   * `territory-period`, otherwise a valid `?quando=` that this URL still
   * carries as a query (Home/category/Esplora/generic surfaces: no
   * period-in-path shape exists there for the pure codec — the server owns
   * the Home→period-path promotion, gated by the w3 pilot via the resolver).
   * Path period always wins over a conflicting query period.
   */
  periodSlug?: string;
  trackingParams: Array<[string, string]>;
  unknownParams: Array<[string, string]>;
  canonicalPath: string;
  /** Canonical query string, '' or starting with '?'. */
  canonicalSearch: string;
  canonicalUrl: string;
}

export type DiscoveryNormalization =
  /** Already at its canonical URL. */
  | { kind: 'canonical'; descriptor: DiscoveryDescriptor }
  /**
   * Needs one hop to `target`: the server issues a 301, the SPA does a
   * replace-navigation — the resolved descriptor is identical either way.
   */
  | { kind: 'redirect'; target: string; descriptor: DiscoveryDescriptor }
  /** Malformed route → 404-class contract, never a generic fallback. */
  | { kind: 'invalid-route'; reason: string }
  /** Valid shape, unknown path category → the existing 404 contract. */
  | { kind: 'not-found'; reason: string }
  /** Recoverable invalid-filter state — the query is NEVER broadened. */
  | { kind: 'invalid-filter'; issues: QueryIssue[] }
  /**
   * Generic entry with a legacy geographic hint: the pure codec cannot
   * resolve geography; a resolver (server registry / client choice) must
   * turn `hint` into a territorial route and re-normalize. `pending` is the
   * fully canonicalized remainder so no filter is lost in the handoff.
   */
  | { kind: 'needs-resolution'; hint: GeographicHint; pending: DiscoveryDescriptor };

function orderCategoryIds(ids: string[]): string[] {
  const unique = Array.from(new Set(ids));
  return unique.sort((a, b) => {
    const sa = categoryIdToQuerySlug(a) ?? a;
    const sb = categoryIdToQuerySlug(b) ?? b;
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  });
}

/**
 * Serializes the canonical query string by hand (NOT URLSearchParams — its
 * toString percent-encodes commas, which would churn every multi-category
 * URL and break idempotency). Free-text values still go through
 * encodeURIComponent. Fixed field order is part of the contract:
 * categorie, quando, startDate, endDate, radiusKm, search, view, mapBbox,
 * then tracking params, then unknown params — each group in encounter order.
 */
function buildSearchFromParts(
  categoryIds: string[],
  categorySource: DiscoveryDescriptor['categorySource'],
  filters: DiscoveryFilters,
  view: 'list' | 'map',
  mapBbox: MapBbox | null,
  queryPeriodSlug: string | undefined,
  trackingParams: Array<[string, string]>,
  unknownParams: Array<[string, string]>
): string {
  const parts: string[] = [];
  if (categorySource === 'query' && categoryIds.length > 0) {
    const slugs = categoryIds.map((id) => categoryIdToQuerySlug(id) ?? id);
    parts.push(`categorie=${slugs.join(',')}`);
  }
  // A period already materialized in the path never re-enters the query
  // (the caller passes undefined in that case — path period wins, one hop).
  if (queryPeriodSlug) parts.push(`quando=${queryPeriodSlug}`);
  if (filters.startDate) parts.push(`startDate=${filters.startDate}`);
  if (filters.endDate && filters.endDate !== filters.startDate) {
    parts.push(`endDate=${filters.endDate}`);
  }
  if (filters.radiusKm !== undefined) parts.push(`radiusKm=${filters.radiusKm}`);
  if (filters.search) parts.push(`search=${encodeURIComponent(filters.search)}`);
  if (view === 'map') parts.push('view=map');
  if (mapBbox) {
    parts.push(`mapBbox=${mapBbox.west},${mapBbox.south},${mapBbox.east},${mapBbox.north}`);
  }
  for (const [key, value] of trackingParams) {
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  for (const [key, value] of unknownParams) {
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

function makeDescriptor(
  route: DiscoveryRoute,
  categoryIds: string[],
  categorySource: DiscoveryDescriptor['categorySource'],
  filters: DiscoveryFilters,
  view: 'list' | 'map',
  mapBbox: MapBbox | null,
  periodSlug: string | undefined,
  queryPeriodSlug: string | undefined,
  trackingParams: Array<[string, string]>,
  unknownParams: Array<[string, string]>
): DiscoveryDescriptor {
  const canonicalPath = buildDiscoveryPath(route);
  const canonicalSearch = buildSearchFromParts(
    categoryIds,
    categorySource,
    filters,
    view,
    mapBbox,
    queryPeriodSlug,
    trackingParams,
    unknownParams
  );
  return {
    route,
    categoryIds,
    categorySource,
    filters,
    view,
    mapBbox,
    ...(periodSlug !== undefined && { periodSlug }),
    trackingParams,
    unknownParams,
    canonicalPath,
    canonicalSearch,
    canonicalUrl: `${canonicalPath}${canonicalSearch}`,
  };
}

/**
 * The single normalization entry point for server and browser.
 *
 * Order of concerns: route shape → query syntax → category precedence
 * (valid path category ALWAYS wins, query category keys are removed, never
 * unioned) → legacy geography (generic entries yield needs-resolution;
 * explicit territorial paths win and strip the keys) → legacy bbox →
 * view/map surface moves → canonical URL comparison.
 */
export function normalizeDiscoveryRequest(input: {
  pathname: string;
  search?: string;
}): DiscoveryNormalization {
  const routeOutcome = parseDiscoveryPath(input.pathname);
  if (routeOutcome.kind === 'invalid') {
    return { kind: 'invalid-route', reason: routeOutcome.reason };
  }
  if (routeOutcome.kind === 'not-found') {
    return { kind: 'not-found', reason: routeOutcome.reason };
  }
  const route = routeOutcome.route;

  const queryOutcome = parseDiscoveryQuery(input.search ?? '');
  if (queryOutcome.kind === 'invalid') {
    return { kind: 'invalid-filter', issues: queryOutcome.issues };
  }
  const query = queryOutcome.query;

  // --- effective period: path always wins over ?quando= (upstream: "periodo
  // nel percorso prioritario"). Computed before any surface move because a
  // category/Esplora move may DEMOTE a path period into the query.
  const pathPeriod = route.kind === 'territory-period' ? route.periodSlug : undefined;
  const effectivePeriod = pathPeriod ?? query.queryPeriodSlug;

  // --- category precedence -------------------------------------------------
  let categoryIds: string[];
  let categorySource: DiscoveryDescriptor['categorySource'];
  if (route.kind === 'territory-category') {
    // Path category wins over `categorie` AND legacy `category` — coincident,
    // conflicting or multi: the query keys are removed, never unioned.
    const id = querySlugToCategoryId(route.categorySlug);
    // parseDiscoveryPath only accepts known SEO slugs, so this is defined.
    categoryIds = id ? [id] : [];
    categorySource = 'path';
  } else if (query.hasCategorieParam) {
    for (const slug of query.categorySlugs) {
      if (!isQueryCategorySlug(slug)) {
        return {
          kind: 'invalid-filter',
          issues: [{ param: 'categorie', reason: 'unknown-category-slug' }],
        };
      }
    }
    const ids = query.categorySlugs.map((slug) => querySlugToCategoryId(slug)!);
    // Coincident legacy `category` collapses; conflicting sets are invalid.
    if (query.legacyCategoryIds) {
      for (const legacyId of query.legacyCategoryIds) {
        if (!isEventCategoryId(legacyId)) {
          return {
            kind: 'invalid-filter',
            issues: [{ param: 'category', reason: 'unknown-category-id' }],
          };
        }
      }
      const legacySlugs = new Set(
        query.legacyCategoryIds.map((id) => categoryIdToQuerySlug(id)!)
      );
      const freshSlugs = new Set(query.categorySlugs);
      const sameSet =
        legacySlugs.size === freshSlugs.size &&
        [...legacySlugs].every((s) => freshSlugs.has(s));
      if (!sameSet) {
        return {
          kind: 'invalid-filter',
          issues: [{ param: 'categorie/category', reason: 'conflicting-category-keys' }],
        };
      }
    }
    categoryIds = orderCategoryIds(ids);
    categorySource = 'query';
  } else if (query.legacyCategoryIds) {
    for (const legacyId of query.legacyCategoryIds) {
      if (!isEventCategoryId(legacyId)) {
        return {
          kind: 'invalid-filter',
          issues: [{ param: 'category', reason: 'unknown-category-id' }],
        };
      }
    }
    categoryIds = orderCategoryIds(query.legacyCategoryIds);
    categorySource = 'query';
  } else {
    categoryIds = [];
    categorySource = 'none';
  }

  // --- legacy geography ----------------------------------------------------
  const isGeneric = route.kind === 'generic-home' || route.kind === 'generic-esplora';
  if (isGeneric && query.legacyGeo && query.centers && query.centers.length > 0) {
    // Conflicting legacy input on a generic entry is never resolved by guess.
    return {
      kind: 'invalid-filter',
      issues: [{ param: 'city/centers', reason: 'conflicting-legacy-geo' }],
    };
  }

  // --- legacy bbox → canonical mapBbox + map view --------------------------
  let view = query.view;
  let mapBbox = query.mapBbox;
  if (!mapBbox && query.legacyBbox) {
    mapBbox = query.legacyBbox;
    view = 'map';
  }

  // --- home entries with map presentation move to the Esplora surface ------
  let effectiveRoute = route;
  if (view === 'map' || mapBbox) {
    if (effectiveRoute.kind === 'generic-home') {
      effectiveRoute = { kind: 'generic-esplora' };
    } else if (effectiveRoute.kind === 'territory-home') {
      effectiveRoute = { kind: 'territory-esplora', segments: effectiveRoute.segments };
    }
  }

  // --- category-driven surface moves ---------------------------------------
  // Category browsing lives on Esplora: a generic Home carrying a selection
  // moves to /esplora. On territory routes a single SEO category becomes the
  // real category path; anything still on territory Home with a query
  // selection (`altro`, or 2+ categories) moves to that territory's Esplora.
  // Zero categories stays exactly where it is (Esplora never collapses to
  // Home, Home never collapses to generic). A PERIOD path behaves like Home
  // here: category > period (upstream precedence), so a query selection
  // takes the path and the period demotes to `?quando=` below.
  if (categorySource === 'query' && categoryIds.length > 0) {
    if (effectiveRoute.kind === 'generic-home') {
      effectiveRoute = { kind: 'generic-esplora' };
    } else if (
      effectiveRoute.kind === 'territory-home' ||
      effectiveRoute.kind === 'territory-period'
    ) {
      const baseSegments = effectiveRoute.segments;
      if (categoryIds.length === 1) {
        const only = categoryIds[0];
        const seo = only !== undefined ? categoryById(only) : undefined;
        if (seo) {
          effectiveRoute = {
            kind: 'territory-category',
            segments: baseSegments,
            categorySlug: seo.slug,
          };
          categorySource = 'path';
        } else {
          effectiveRoute = {
            kind: 'territory-esplora',
            segments: baseSegments,
          };
        }
      } else {
        effectiveRoute = {
          kind: 'territory-esplora',
          segments: baseSegments,
        };
      }
    }
  }

  // --- period promotion (SERVER-OWNED, NOT HERE) ----------------------------
  // A valid ?quando= on a territory Home is NOT promoted to the period-in-path
  // URL by the pure codec: whether that path exists is the w3 pilot decision,
  // and the codec carries no pilot data. The codec keeps the period as a
  // recognized query on the descriptor (`periodSlug`); the server applies the
  // same resolver it always used — when the resolver yields the period (in
  // scope), api/ssr 301s to the period-in-path URL and api/territory reports
  // it as normalizedPath; when the resolver ignores the query (out of scope)
  // the plain Home renders exactly like the legacy lane, and widening the
  // rollout starts the 301 with no code change.

  // A period materialized in the path never re-enters the query; a period
  // still living in the query (Home/Esplora/generic/category, or demoted from
  // the path by a category move) is serialized here, right after `categorie`.
  const emitQueryPeriod =
    effectivePeriod !== undefined && effectiveRoute.kind !== 'territory-period'
      ? effectivePeriod
      : undefined;

  // --- build the canonical descriptor (category query keys already gone) ---
  const descriptor = makeDescriptor(
    effectiveRoute,
    categoryIds,
    categorySource,
    query.filters,
    view,
    mapBbox,
    effectivePeriod,
    emitQueryPeriod,
    query.trackingParams,
    query.unknownParams
  );

  // --- legacy geo on generic entries: hand off typed, never national -------
  if (isGeneric) {
    if (query.legacyGeo) {
      const hint: GeographicHint = query.legacyGeo.province
        ? { kind: 'pair', city: query.legacyGeo.city, province: query.legacyGeo.province }
        : { kind: 'pair', city: query.legacyGeo.city };
      return { kind: 'needs-resolution', hint, pending: descriptor };
    }
    if (query.centers && query.centers.length > 0) {
      const first = query.centers[0];
      if (first) {
        return {
          kind: 'needs-resolution',
          hint: { kind: 'center', lat: first.lat, lng: first.lng, total: query.centers.length },
          pending: descriptor,
        };
      }
    }
  }
  // Territorial path wins: legacy city/province/centers keys are simply
  // absent from the canonical descriptor (stripped after replacement).

  // --- canonical vs one-hop redirect ---------------------------------------
  const inputSearch = input.search
    ? input.search.startsWith('?')
      ? input.search
      : `?${input.search}`
    : '';
  const inputPath = stripTrailingSlash(input.pathname.split('?')[0] ?? input.pathname);
  const inputUrl = `${inputPath}${inputSearch}`;
  if (inputUrl === descriptor.canonicalUrl) {
    return { kind: 'canonical', descriptor };
  }
  return { kind: 'redirect', target: descriptor.canonicalUrl, descriptor };
}

/**
 * Builds the territorial replacement for a generic entry from an optional
 * saved comune path + saved radius (the visit/saved-choice build logic the
 * client runs — a pure function here so both sides agree). No saved path →
 * the entry stays national. A saved radius differing from the territory
 * preset is materialized in the URL; an equal one is omitted.
 */
export function buildGenericEntryReplacement(input: {
  entry: 'home' | 'esplora';
  savedComunePath?: string | null;
  savedRadiusKm?: number | null;
  presetRadiusKm?: number | null;
}): string {
  if (!input.savedComunePath) return input.entry === 'home' ? '/' : '/esplora';
  const base = stripTrailingSlash(input.savedComunePath);
  const path = input.entry === 'home' ? base : `${base}/${ESPLORA_RESERVED_SEGMENT}`;
  const radius = input.savedRadiusKm;
  if (
    radius !== undefined &&
    radius !== null &&
    radius >= RADIUS_KM_MIN && // a corrupted store must not materialize a URL
    radius <= RADIUS_KM_MAX && // that immediately normalizes to invalid-filter
    radius !== input.presetRadiusKm
  ) {
    return `${path}?radiusKm=${radius}`;
  }
  return path;
}

// ---------------------------------------------------------------------------
// List/map descriptors (R3: viewport is explicit, list keeps territory+radius)
// ---------------------------------------------------------------------------

export interface ListScopeDescriptor {
  kind: 'list';
  radiusKm: number | undefined;
  categoryIds: string[];
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface MapScopeDescriptor {
  kind: 'map';
  bbox: MapBbox;
  categoryIds: string[];
  startDate?: string;
  endDate?: string;
  search?: string;
}

/**
 * List scope = selected territory + radius. NEVER carries the viewport:
 * `mapBbox` does not change list filtering (desktop contract, T014).
 * `defaultRadiusKm` is the registry preset applied when the URL omits radius.
 */
export function listScope(
  descriptor: DiscoveryDescriptor,
  defaultRadiusKm?: number
): ListScopeDescriptor {
  return {
    kind: 'list',
    radiusKm: descriptor.filters.radiusKm ?? defaultRadiusKm,
    categoryIds: descriptor.categoryIds,
    ...(descriptor.filters.startDate ? { startDate: descriptor.filters.startDate } : {}),
    ...(descriptor.filters.endDate ? { endDate: descriptor.filters.endDate } : {}),
    ...(descriptor.filters.search ? { search: descriptor.filters.search } : {}),
  };
}

/**
 * Map scope exists only with an explicit bbox (an incoming mapBbox or a
 * legacy bbox translation). Before any explicit viewport the map uses the
 * list's spatial query — so this returns null and the caller falls back.
 * Carries the same category/date/search constraints as the list.
 */
export function mapScope(descriptor: DiscoveryDescriptor): MapScopeDescriptor | null {
  if (!descriptor.mapBbox) return null;
  return {
    kind: 'map',
    bbox: descriptor.mapBbox,
    categoryIds: descriptor.categoryIds,
    ...(descriptor.filters.startDate ? { startDate: descriptor.filters.startDate } : {}),
    ...(descriptor.filters.endDate ? { endDate: descriptor.filters.endDate } : {}),
    ...(descriptor.filters.search ? { search: descriptor.filters.search } : {}),
  };
}

/** Canonical search string of a descriptor ('' or '?…'). */
export function buildDiscoverySearch(descriptor: DiscoveryDescriptor): string {
  return descriptor.canonicalSearch;
}

/** Canonical URL of a descriptor (path + search). */
export function buildDiscoveryUrl(descriptor: DiscoveryDescriptor): string {
  return descriptor.canonicalUrl;
}

// ---------------------------------------------------------------------------
// SEO variant helpers (T009/T016 consume these)
// ---------------------------------------------------------------------------

/**
 * True when the URL carries an effective filter: query categories, dates,
 * radius, search, or map presentation. A PATH category is the page's
 * identity, not a filter — category pages stay indexable candidates.
 */
export function hasEffectiveFilters(descriptor: DiscoveryDescriptor): boolean {
  return (
    (descriptor.categorySource === 'query' && descriptor.categoryIds.length > 0) ||
    // endDate alone is an effective filter too (parseDiscoveryQuery accepts
    // ?endDate= without startDate — the same-day default only fills end from
    // start, not the reverse), so it must disqualify indexability.
    descriptor.filters.startDate !== undefined ||
    descriptor.filters.endDate !== undefined ||
    descriptor.filters.radiusKm !== undefined ||
    descriptor.filters.search !== undefined ||
    descriptor.view === 'map' ||
    descriptor.mapBbox !== null
  );
}

/**
 * True when the URL is an indexable-variant candidate: no effective filters,
 * no unknown params, and not a general-Esplora surface (unfiltered Esplora
 * consolidates to its Home canonical and is omitted from the sitemap —
 * actual indexability also depends on registry thresholds, server-side).
 * Tracking-only params do NOT disqualify (tracking stays canonical).
 */
export function isIndexableVariant(descriptor: DiscoveryDescriptor): boolean {
  if (hasEffectiveFilters(descriptor)) return false;
  if (descriptor.unknownParams.length > 0) return false;
  if (descriptor.route.kind === 'generic-esplora') return false;
  if (descriptor.route.kind === 'territory-esplora') return false;
  return true;
}

// ---------------------------------------------------------------------------
// LIVE-CLIENT EPOCH CONTRACT (T003/T005/T007/T008/T009/T011/T016/T018)
// ---------------------------------------------------------------------------

/** The discovery protocol version this shared build speaks. */
export const DISCOVERY_PROTOCOL_VERSION = 1;

/** Request headers a discovery client MUST send after activation. */
export const DISCOVERY_PROTOCOL_HEADER = 'x-discovery-protocol';
export const REGISTRY_VERSION_HEADER = 'x-registry-version';

/** Query-string fallback when headers are unavailable. */
export const DISCOVERY_PROTOCOL_QUERY_PARAM = 'discoveryProtocol';
export const REGISTRY_VERSION_QUERY_PARAM = 'registryVersion';

/** Capabilities responses (`GET /api/territory?mode=capabilities`) are… */
export const CAPABILITIES_CACHE_CONTROL = 'no-store';
export const CAPABILITIES_ROBOTS_TAG = 'noindex';

/** HTTP status of the CLIENT_UPGRADE_REQUIRED error envelope. */
export const UPGRADE_REQUIRED_HTTP_STATUS = 426;
export const CLIENT_UPGRADE_REQUIRED_ERROR = 'CLIENT_UPGRADE_REQUIRED';

/**
 * Body of the no-store capabilities response — the server's registry epoch
 * advertisement. `ready:false` means the current registry is not yet
 * authoritative; clients must show the maintenance/upgrade state.
 */
export interface TerritoryCapabilities {
  minimumDiscoveryProtocol: number;
  activeRegistryVersion: string;
  ready: boolean;
}

/** Client → server compatibility metadata (headers, query fallback). */
export interface DiscoveryRequestMeta {
  discoveryProtocol?: number;
  registryVersion?: string;
}

export type ProtocolNegotiationOutcome =
  /** Protocol supported and registry version pinned to the active one. */
  | { kind: 'ok' }
  /** Protocol supported, but the client pinned another registry version. */
  | { kind: 'reference-changed'; activeRegistryVersion: string }
  /** Unsupported/missing protocol, or the registry is not ready. */
  | { kind: 'upgrade-required'; minimumDiscoveryProtocol: number };

/** 426 error envelope — shared shape for server and client. */
export interface ClientUpgradeRequiredError {
  error: typeof CLIENT_UPGRADE_REQUIRED_ERROR;
  minimumDiscoveryProtocol: number;
  /** Same-origin path that serves a fresh, compatible bundle. */
  refreshUrl: string;
}

/**
 * Reads compatibility metadata from request headers with a query-string
 * fallback. This marker is compatibility metadata only — NOT authentication
 * and NOT validation of any supplied geography; descriptors are validated
 * exactly as before regardless of what the client claims.
 */
export function readDiscoveryRequestMeta(
  headers: Record<string, string | string[] | undefined>,
  query?: Record<string, string | string[] | undefined>
): DiscoveryRequestMeta {
  const headerValue = headers[DISCOVERY_PROTOCOL_HEADER];
  const queryValue = query?.[DISCOVERY_PROTOCOL_QUERY_PARAM];
  const rawProtocol = Array.isArray(headerValue)
    ? headerValue[0]
    : (headerValue ?? (Array.isArray(queryValue) ? queryValue[0] : queryValue));
  const headerRegistry = headers[REGISTRY_VERSION_HEADER];
  const queryRegistry = query?.[REGISTRY_VERSION_QUERY_PARAM];
  const rawRegistry = Array.isArray(headerRegistry)
    ? headerRegistry[0]
    : (headerRegistry ?? (Array.isArray(queryRegistry) ? queryRegistry[0] : queryRegistry));

  const meta: DiscoveryRequestMeta = {};
  if (rawProtocol !== undefined) {
    const parsed = Number(rawProtocol);
    if (Number.isFinite(parsed)) meta.discoveryProtocol = parsed;
  }
  if (typeof rawRegistry === 'string' && rawRegistry.length > 0) {
    meta.registryVersion = rawRegistry;
  }
  return meta;
}

/**
 * Negotiates protocol and registry version. Format translation (legacy URL
 * keys) is NOT semantic compatibility — only this negotiation, against the
 * server's real capabilities, proves a client can share the current
 * geographic semantics. After epoch activation, `upgrade-required` responses
 * are rejected BEFORE any geographic query runs.
 */
export function negotiateDiscoveryProtocol(
  meta: DiscoveryRequestMeta,
  capabilities: TerritoryCapabilities
): ProtocolNegotiationOutcome {
  if (!capabilities.ready) {
    return {
      kind: 'upgrade-required',
      minimumDiscoveryProtocol: capabilities.minimumDiscoveryProtocol,
    };
  }
  const protocol = meta.discoveryProtocol;
  if (protocol === undefined || protocol < capabilities.minimumDiscoveryProtocol) {
    return {
      kind: 'upgrade-required',
      minimumDiscoveryProtocol: capabilities.minimumDiscoveryProtocol,
    };
  }
  if (meta.registryVersion !== capabilities.activeRegistryVersion) {
    return { kind: 'reference-changed', activeRegistryVersion: capabilities.activeRegistryVersion };
  }
  return { kind: 'ok' };
}

/**
 * Conservative charset for the 426 `refreshUrl`: a leading '/' plus only
 * path/query-safe ASCII. Anything else collapses to '/' — in particular a
 * raw backslash (WHATWG URL parsers normalize `\` to `/`, so `/\evil.com`
 * becomes `//evil.com` → off-origin) and C0 control characters (tab/CR/LF
 * are STRIPPED by the URL preprocessor, so `/<TAB>//evil.com` collapses to
 * `//evil.com` → off-origin). The `//` protocol-relative prefix is rejected
 * separately below.
 */
const SAFE_REFRESH_PATH_RE = /^\/[A-Za-z0-9\-._~/%?&=]*$/;

/**
 * Builds the 426 envelope. `refreshUrl` must be a same-origin absolute path;
 * anything else (absolute URL, protocol-relative, backslash, control
 * characters, or any character outside the conservative charset) collapses
 * to '/'. This is the contract's only enforcement point for the "forged
 * redirect targets" threat — future callers may pass request-derived paths,
 * so the guard must not be weakened.
 */
export function buildUpgradeRequiredError(
  capabilities: TerritoryCapabilities,
  refreshUrl: string
): ClientUpgradeRequiredError {
  const safePath =
    refreshUrl.startsWith('/') &&
    !refreshUrl.startsWith('//') &&
    SAFE_REFRESH_PATH_RE.test(refreshUrl)
      ? refreshUrl
      : '/';
  return {
    error: CLIENT_UPGRADE_REQUIRED_ERROR,
    minimumDiscoveryProtocol: capabilities.minimumDiscoveryProtocol,
    refreshUrl: safePath,
  };
}

/** Response headers for the capabilities endpoint (no-store + noindex). */
export function buildCapabilitiesHeaders(): Record<string, string> {
  return {
    'Cache-Control': CAPABILITIES_CACHE_CONTROL,
    'X-Robots-Tag': CAPABILITIES_ROBOTS_TAG,
  };
}
